import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import { Typography } from "@mui/material";
import TreeView from "@mui/lab/TreeView";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import TreeItem, { treeItemClasses } from "@mui/lab/TreeItem";
import MzIcon from "mz/MzIcon";

const activeIconFilter = "invert(63%) sepia(89%) saturate(2124%) hue-rotate(145deg) brightness(125%) contrast(106%)";

const sx = {
  root: {
    backgroundColor: "darkBlue.c100",
    color: "common.white",
    width: "300px",
    height: "calc(100vh - 80px)",
    overflowX: "hidden",
    overflowY: "scroll",
    "&::-webkit-scrollbar": {
      width: "0px"
    }
  },
  treeView: {
    [`.${treeItemClasses.root} .${treeItemClasses.group}`]: {
      marginLeft: 2
    },
    [`.${treeItemClasses.content}`]: {
      padding: 0,
      margin: "16px 8px",
      maxWidth: "284px",

      [`&.Mui-expanded .icon`]: {
        filter: activeIconFilter
      },

      [`.${treeItemClasses.label}`]: {
        padding: "8px 8px",
        maxWidth: "284px"
      },

      [`&.Mui-selected`]: {
        backgroundColor: (theme) => theme.palette.darkBlue.c100
      },

      [`&.${treeItemClasses.selected}.${treeItemClasses.focused}`]: {
        backgroundColor: (theme) => theme.palette.darkBlue.c100
      }
    },
    [`> .${treeItemClasses.root} > .${treeItemClasses.content}`]: {
      flexDirection: "row-reverse",
      [`.${treeItemClasses.label}`]: {
        padding: "10px 8px"
      },
      [`&.Mui-selected`]: {
        borderRadius: 2
      },
      [`.${treeItemClasses.iconContainer} svg`]: {
        marginRight: 10
      }
    }
  },
  label: {
    display: "flex",
    alignItems: "center",
    gap: 2
  },
  labelActive: {
    color: "acqua.c100",
    [`.icon`]: {
      filter: activeIconFilter
    }
  },
  beta: {
    minWidth: "fit-content"
  },
  levelZeroMenuMargin: "16px 8px",
  levelOneMenuMargin: "8px 8px 16px 8px",
  levelOneMenuMargin2: "8px 8px 16px 42px",
  levelTwoMenuMargin: "8px 8px 16px 26px"
};

export default function Sidebar({ currentPath, menuItems, onClick }) {
  const [expanded, setExpanded] = useState([]);

  useEffect(() => {
    const activeIdPath = getCurrentPathNode(menuItems, currentPath) || [];
    setExpanded(activeIdPath);
  }, [currentPath, menuItems]);

  function handleClick(node) {
    if (node.path) {
      onClick(node.path);
    } else {
      if (expanded === node.nodePath) {
        setExpanded(node.nodePath.slice(0, -1));
      } else if (expanded.length && node.nodePath.every((r) => expanded.includes(r))) {
        setExpanded(node.nodePath.slice(0, -1));
      } else {
        setExpanded(node.nodePath);
      }
    }
  }

  return (
    <Box sx={sx.root}>
      <TreeView sx={sx.treeView} selected={expanded.at(-1) || null} expanded={expanded}>
        {RenderTreeItem(menuItems, currentPath, handleClick, 0)}
      </TreeView>
    </Box>
  );
}

function RenderTreeItem(nodes, currentPath, onClick, level = 0) {
  return (
    <>
      {nodes.map((node, index) => {
        if (!node.visible) return null;

        let itemSx =
          index !== 0 && level === 0
            ? { borderTop: (theme) => `1px solid ${theme.palette.blue.c20}80` }
            : {
                [`.${treeItemClasses.content}`]: {
                  margin: sx.levelOneMenuMargin
                }
              };

        if (!Array.isArray(node.items)) {
          itemSx = {
            ...itemSx,
            [`.${treeItemClasses.content}`]: {
              width: "284px",
              margin: sx.levelZeroMenuMargin,

              [`&.Mui-selected`]: {
                backgroundColor: (theme) => `${theme.palette.blue.c20}33`,
                borderRadius: 2
              },

              [`&.${treeItemClasses.selected}.${treeItemClasses.focused}`]: {
                backgroundColor: (theme) => `${theme.palette.blue.c20}33`,
                borderRadius: 2
              }
            }
          };

          if (level === 1) {
            itemSx[`.${treeItemClasses.content}`].width = "243px";
            itemSx[`.${treeItemClasses.content}`].margin = sx.levelOneMenuMargin2;
          } else if (level === 2) {
            itemSx[`.${treeItemClasses.content}`].width = "248px";
            itemSx[`.${treeItemClasses.content}`].margin = sx.levelTwoMenuMargin;
          }
        }

        return (
          <TreeItem
            sx={itemSx}
            key={node.id}
            nodeId={node.id}
            expandIcon={node.icon ? <AddIcon /> : <ChevronRightIcon />}
            collapseIcon={node.icon ? <RemoveIcon /> : <ExpandMoreIcon />}
            label={<TreeLabel {...node} currentPath={currentPath} />}
            onClick={() => {
              onClick(node);
            }}
          >
            {Array.isArray(node.items) ? RenderTreeItem(node.items, currentPath, onClick, level + 1) : null}
          </TreeItem>
        );
      })}
    </>
  );
}

function TreeLabel({ title, icon, path, currentPath, beta }) {
  const sxLabel = [sx.label];

  if (currentPath === path) {
    sxLabel.push(sx.labelActive);
  }

  return (
    <Box sx={sxLabel}>
      <MzIcon className="icon" name={icon}></MzIcon>
      <Typography variant={icon ? "body1M" : "body2M"} sx={{ color: icon && "common.white" }}>
        {title}
      </Typography>
      <MzIcon name={beta} sx={sx.beta} />
    </Box>
  );
}

function getCurrentPathNode(nodes, currentPath) {
  let result = null;

  nodes.forEach((node) => {
    if (currentPath === node.path) {
      result = node.nodePath;
    }

    if (!result && node.items) {
      result = getCurrentPathNode(node.items, currentPath);
    }
  });

  return result;
}
